<template>
    <div>
        <ts-page-title
            :title="$t('dailyCloseout.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('dailyCloseout.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <div
                    class="tw-flex tw-justify-start tw-space-x-2 tw-whitespace-nowrap"
                >
                    <ts-button
                        color="warning"
                        @click="
                            $router.push({
                                name: 'daily-closeout-create'
                            })
                        "
                        >{{ $t("addNew") }}
                    </ts-button>
                    <DatePicker
                        placement="bottom-end"
                        style="width: 200px"
                        :transfer="true"
                        format="dd-MM-yyyy"
                        @on-change="onChangeActualPaymentDate"
                        v-model="actual_payment_date"
                    ></DatePicker>
                </div>
                <div class="tw-flex tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('dailyCloseout.search')"
                        style="width: 300px"
                        enter-button
                        @on-search="onSearch"
                    />
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
                border
            >
            </Table>
            <div class="tw-flex tw-items-center tw-justify-between tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                    :show-sizer="false"
                ></ts-i-pagination>
            </div>
        </ts-panel>
        
        <ReceiptModal 
            v-if="receiptData" 
            :visible.sync="showReceipt" 
            :receipt="receiptData" 
            @close="closeReceipt" 
        />
    </div>
</template>

<script>
import { mapState } from "vuex";
import ReceiptModal from "./ReceiptModal-Simple.vue";
import moment from "moment";

export default {
    components: { ReceiptModal },
    name: "dailyCloseoutIndex",
    data() {
        return {
            loading: false,
            actual_payment_date: moment().format("DD-MM-YYYY"),
            showReceipt: false,
            receiptData: null
        };
    },
    computed: {
        ...mapState("creditOperation/dailyCloseout", [
            "pagination"
        ]),
        search: {
            get() {
                return this.$store.state.creditOperation.dailyCloseout.search;
            },
            set(newValue) {
                this.$store.commit(
                    "creditOperation/dailyCloseout/SET_SEARCH",
                    newValue
                );
                this.$store.commit(
                    "creditOperation/dailyCloseout/RESET_CURRENT_PAGE"
                );
            }
        },
        columns() {
            return [
                {
                    title: "#",
                    type: "index",
                    width: 60,
                    align: "center"
                },
                {
                    title: this.$t("dailyCloseout.reportDate"),
                    key: "report_date",
                    width: 120,
                    align: "center",
                    sortable: true,
                    render: (h, params) => {
                        return h('span', this.formatDate(params.row.report_date));
                    }
                },
                {
                    title: this.$t("dailyCloseout.branch"),
                    width: 140,
                    render: (h, params) => {
                        return h('div', [
                            h('div', { class: 'tw-font-semibold tw-text-xs' }, params.row.branch ? params.row.branch.branch_name_en : '-'),
                            h('div', { class: 'tw-text-xs tw-text-gray-500' }, params.row.branch ? params.row.branch.branch_code : '')
                        ]);
                    }
                },
                {
                    title: this.$t("dailyCloseout.coNameEn"),
                    minWidth: 180,
                    render: (h, params) => {
                        const co = params.row.collection_officer;
                        return h('div', [
                            h('div', { class: 'tw-font-semibold tw-text-xs' }, co ? co.employee_name_en : '-'),
                            h('div', { class: 'tw-text-xs tw-text-gray-500' }, co ? co.card_id : '')
                        ]);
                    }
                },
                {
                    title: "Disbursement",
                    align: "center",
                    children: [
                        {
                            title: "Requested",
                            key: "loan_requested_amount",
                            width: 110,
                            align: "right",
                            render: (h, params) => {
                                return h('span', this.formatNumber(params.row.loan_requested_amount));
                            }
                        },
                        {
                            title: "Amount",
                            key: "disbursement_amount",
                            width: 110,
                            align: "right",
                            render: (h, params) => {
                                return h('span', this.formatNumber(params.row.disbursement_amount));
                            }
                        },
                        {
                            title: "Returned",
                            key: "returned_amount",
                            width: 100,
                            align: "right",
                            render: (h, params) => {
                                return h('span', { class: params.row.returned_amount < 0 ? 'tw-text-red-600' : '' }, 
                                    this.formatNumber(params.row.returned_amount));
                            }
                        },
                        {
                            title: "Actual",
                            key: "actual_disbursed_amount",
                            width: 110,
                            align: "right",
                            render: (h, params) => {
                                return h('span', this.formatNumber(params.row.actual_disbursed_amount));
                            }
                        }
                    ]
                },
                {
                    title: "Collection",
                    align: "center",
                    children: [
                        {
                            title: "Disburse",
                            key: "disburse_collected_amount",
                            width: 110,
                            align: "right",
                            render: (h, params) => {
                                return h('span', this.formatNumber(params.row.disburse_collected_amount));
                            }
                        },
                        {
                            title: "Actual",
                            key: "actual_collected_total",
                            width: 110,
                            align: "right",
                            render: (h, params) => {
                                return h('span', { class: 'tw-font-semibold tw-text-green-600' }, 
                                    this.formatNumber(params.row.actual_collected_total));
                            }
                        }
                    ]
                },
                {
                    title: "Cash",
                    align: "center",
                    children: [
                        {
                            title: "KHR",
                            width: 100,
                            align: "right",
                            render: (h, params) => {
                                const summary = params.row.actual_collected_summary;
                                const cashKhr = summary && summary.cash ? summary.cash.khr : 0;
                                return h('span', { class: cashKhr > 0 ? 'tw-text-yellow-600 tw-font-semibold' : '' }, 
                                    this.formatNumber(cashKhr));
                            }
                        },
                        {
                            title: "USD",
                            width: 90,
                            align: "right",
                            render: (h, params) => {
                                const summary = params.row.actual_collected_summary;
                                const cashUsd = summary && summary.cash ? summary.cash.usd : 0;
                                return h('span', { class: cashUsd > 0 ? 'tw-text-yellow-600 tw-font-semibold' : '' }, 
                                    this.formatNumber(cashUsd, 'USD'));
                            }
                        }
                    ]
                },
                {
                    title: "ABA Bank",
                    align: "center",
                    children: [
                        {
                            title: "KHR",
                            width: 100,
                            align: "right",
                            render: (h, params) => {
                                const summary = params.row.actual_collected_summary;
                                const abaKhr = summary && summary.bank_aba ? summary.bank_aba.khr : 0;
                                return h('span', { class: abaKhr > 0 ? 'tw-text-blue-600 tw-font-semibold' : '' }, 
                                    this.formatNumber(abaKhr));
                            }
                        },
                        {
                            title: "USD",
                            width: 90,
                            align: "right",
                            render: (h, params) => {
                                const summary = params.row.actual_collected_summary;
                                const abaUsd = summary && summary.bank_aba ? summary.bank_aba.usd : 0;
                                return h('span', { class: abaUsd > 0 ? 'tw-text-blue-600 tw-font-semibold' : '' }, 
                                    this.formatNumber(abaUsd, 'USD'));
                            }
                        }
                    ]
                },
                {
                    title: "ACLEDA Bank",
                    align: "center",
                    children: [
                        {
                            title: "KHR",
                            width: 100,
                            align: "right",
                            render: (h, params) => {
                                const summary = params.row.actual_collected_summary;
                                const acledaKhr = summary && summary.bank_acleda ? summary.bank_acleda.khr : 0;
                                return h('span', { class: acledaKhr > 0 ? 'tw-text-purple-600 tw-font-semibold' : '' }, 
                                    this.formatNumber(acledaKhr));
                            }
                        },
                        {
                            title: "USD",
                            width: 100,
                            align: "right",
                            render: (h, params) => {
                                const summary = params.row.actual_collected_summary;
                                const acledaUsd = summary && summary.bank_acleda ? summary.bank_acleda.usd : 0;
                                return h('span', { class: acledaUsd > 0 ? 'tw-text-purple-600 tw-font-semibold' : '' }, 
                                    this.formatNumber(acledaUsd, 'USD'));
                            }
                        }
                    ]
                },
                {
                    title: "Cleared By",
                    width: 140,
                    render: (h, params) => {
                        const receiver = params.row.receiver;
                        if (!receiver) return h('span', { class: 'tw-text-gray-400' }, '-');
                        
                        const name = receiver.employee ? receiver.employee.employee_name_en : receiver.name;
                        const date = this.formatDateTime(params.row.created_date);
                        
                        return h('div', [
                            h('div', { class: 'tw-text-xs tw-font-medium' }, name),
                            h('div', { class: 'tw-text-xs tw-text-gray-500' }, date)
                        ]);
                    }
                },
                {
                    title: this.$t("dailyCloseout.status"),
                    key: "is_closed",
                    width: 90,
                    align: "center",
                    fixed: "right",
                    render: (h, params) => {
                        return h('i-tag', {
                            props: {
                                color: params.row.is_closed ? 'success' : 'warning'
                            }
                        }, params.row.is_closed ? 'Closed' : 'Open');
                    }
                },
                {
                    title: this.$t("actions"),
                    width: 205,
                    align: "center",
                    fixed: "right",
                    render: (h, params) => {
                        return h('div', { class: 'tw-space-x-1' }, [
                            h('i-button', {
                                props: {
                                    type: 'info',
                                    size: 'small',
                                    icon: 'md-document'
                                },
                                on: {
                                    click: () => {
                                        this.showReceiptModal(params.row);
                                    }
                                }
                            }, 'Receipt'),
                            h('i-poptip', {
                                props: {
                                    confirm: true,
                                    title: 'Are you sure you want to delete this record?',
                                    transfer: true
                                },
                                on: {
                                    'on-ok': () => {
                                        this.onDelete(params.row);
                                    }
                                }
                            }, [
                                h('i-button', {
                                    props: {
                                        type: 'error',
                                        size: 'small',
                                        icon: 'md-trash',
                                        disabled: params.row.is_closed
                                    }
                                }, this.$t('delete'))
                            ])
                        ]);
                    }
                }
            ];
        },
        resources() {
            return this.$store.state.creditOperation.dailyCloseout.resources || [];
        }
    },
    methods: {
        formatNumber(num, currency = "") {
            if ((!num && num !== 0) || num === 0) return "-";
            let val = new Intl.NumberFormat("en-US", { 
                minimumFractionDigits: currency === "USD" ? 2 : 0, 
                maximumFractionDigits: 2 
            }).format(num);
            return val;
        },
        formatDate(date) {
            if (!date) return '';
            return moment(date).format('DD-MMM-YYYY');
        },
        formatDateTime(date) {
            if (!date) return '';
            return moment(date).format('DD-MMM-YYYY HH:mm');
        },
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("creditOperation/dailyCloseout/fetch", {
                    ...attributes,
                    report_date: this.actual_payment_date
                })
                .catch(error => {
                    this.loading = false;
                    this.$Message.error(error.message || 'An error occurred');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onChangeActualPaymentDate(date) {
            this.actual_payment_date = date;
            this.fetchData({
                report_date: date
            });
        },
        showReceiptModal(record) {
            const summary = record.actual_collected_summary || {
                cash: { khr: 0, usd: 0 },
                bank_aba: { khr: 0, usd: 0 },
                bank_acleda: { khr: 0, usd: 0 }
            };
            
            this.receiptData = {
                id: record.id,
                report_date: this.formatDate(record.report_date),
                cleared_at: this.formatDateTime(record.created_date),
                branch: {
                    code: record.branch ? record.branch.branch_code : '',
                    name: record.branch ? record.branch.branch_name_en : '',
                },
                co: {
                    code: record.collection_officer ? record.collection_officer.card_id : '',
                    name: record.collection_officer ? record.collection_officer.employee_name_en : '',
                },
                approved_by: {
                    name: record.approver ? record.approver.name : '-',
                },
                received_by: {
                    name: record.receiver ? (record.receiver.employee ? record.receiver.employee.employee_name_en : record.receiver.name) : '-',
                },
                amounts: {
                    cash_khr: parseFloat(summary.cash.khr || 0),
                    cash_usd: parseFloat(summary.cash.usd || 0),
                    loan_requested: parseFloat(record.loan_requested_amount || 0),
                    disbursement: parseFloat(record.disbursement_amount || 0),
                    returned: parseFloat(record.returned_amount || 0),
                    actual_disbursed: parseFloat(record.actual_disbursed_amount || 0),
                    disburse_collected: parseFloat(record.disburse_collected_amount || 0),
                    actual_collected: parseFloat(record.actual_collected_total || 0),
                    actual_collected_total: parseFloat(record.actual_collected_total || 0),
                },
                payment_methods: {
                    cash: {
                        khr: parseFloat(summary.cash.khr || 0),
                        usd: parseFloat(summary.cash.usd || 0)
                    },
                    bank_aba: {
                        khr: parseFloat(summary.bank_aba.khr || 0),
                        usd: parseFloat(summary.bank_aba.usd || 0)
                    },
                    bank_acleda: {
                        khr: parseFloat(summary.bank_acleda.khr || 0),
                        usd: parseFloat(summary.bank_acleda.usd || 0)
                    }
                }
            };
            this.showReceipt = true;
        },
        closeReceipt() {
            this.showReceipt = false;
            this.$nextTick(() => {
                this.receiptData = null;
            });
        },
        onDelete(record) {
            this.loading = true;
            this.$store
                .dispatch('creditOperation/dailyCloseout/destroy', {
                    id: record.id,
                    data: {}
                })
                .then(response => {
                    if (response.success) {
                        this.$Message.success('Closeout deleted successfully');
                        this.fetchData();
                    } else {
                        this.$Message.error(response.message || 'Failed to delete closeout');
                    }
                })
                .catch(error => {
                    this.$Message.error(error.message || 'An error occurred while deleting');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onSearch() {
            if (!this.search) return;
            this.fetchData({
                search: this.search,
                report_date: this.actual_payment_date
            });
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData();
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit("creditOperation/dailyCloseout/RESET_STATE");
        next();
    }
};
</script>
<style scoped>
.text-nowrap {
    white-space: nowrap;
}
</style>
